/**
 * RemovableDashedTag Component
 *
 * A local implementation of a removable tag with integrated X button.
 * This component follows the UX design pattern where the remove button
 * is integrated within the tag boundary, not floating outside.
 *
 * Features:
 * - Hover-reveal X button integrated within tag boundary
 * - Uppercase text styling consistent with DashedTag
 * - Full keyboard accessibility (Delete/Backspace support)
 * - Proper ARIA labels and focus management
 * - Smooth transitions (200ms) for professional feel
 *
 * @example
 * ```tsx
 * <RemovableDashedTag
 *   label="production"
 *   size="sm"
 *   onRemove={() => handleRemove('production')}
 * />
 * ```
 */

import { XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/utils/classname';

interface RemovableDashedTagProps {
  label: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onRemove: () => void;
  className?: string;
}

// Size classes helper matching chariot-ui-components pattern
const getSizeClasses = (size: 'xs' | 'sm' | 'md' | 'lg' = 'sm') => {
  const sizes = {
    xs: 'py-1 px-2 text-[10px]',
    sm: 'py-1.5 px-3 text-xs',
    md: 'py-2 px-4 text-xs',
    lg: 'py-2.5 px-5 text-sm',
  };
  return sizes[size];
};

export const RemovableDashedTag: React.FC<RemovableDashedTagProps> = ({
  label,
  onRemove,
  size = 'sm',
  className = '',
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      onRemove();
    }
  };

  return (
    <div
      role="group"
      aria-label={`${label} tag`}
      data-testid="removable-tag"
      className={cn(
        'group inline-flex items-center gap-1.5',
        'border-chariot-stroke rounded-full border-2 border-dashed',
        'text-chariot-text-secondary leading-3 font-semibold uppercase',
        'bg-transparent',
        'transition-all duration-200',
        'hover:border-chariot-text-secondary hover:bg-chariot-background-secondary/30',
        getSizeClasses(size),
        className
      )}
    >
      <span className="transition-colors">{label}</span>
      <button
        onClick={e => {
          e.stopPropagation();
          onRemove();
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          '-mr-1 rounded-full p-0.5',
          'text-chariot-text-secondary',
          'hover:bg-chariot-background-primary hover:text-chariot-text-primary',
          'focus-visible:ring-chariot-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          'transition-all duration-200',
          'cursor-pointer'
        )}
        aria-label={`Remove ${label} tag`}
        data-testid="remove-tag-button"
        tabIndex={0}
        type="button"
      >
        <XMarkIcon className="size-3" aria-hidden="true" />
      </button>
    </div>
  );
};
